export interface WhatsAppConfig {
  /** E.164-style phone numbers without the leading '+' */
  numbers: string[];
  /** Plain text message (will be URI-encoded automatically) */
  message: string;
  /**
   * Optional namespace for localStorage — use different keys if you have
   * multiple WhatsApp buttons on the same site with different number pools.
   * Defaults to "default".
   */
  namespace?: string;
}

/**
 * Returns a WhatsApp deep-link URL.
 *
 * Assignment strategy: random on first visit, then sticky via localStorage.
 * The same browser keeps the same number across sessions; different browsers
 * and devices each draw independently, which distributes load evenly regardless
 * of how similar the devices are (same phone model, OS, carrier, timezone, etc.).
 */
export function getWhatsAppLink(config: WhatsAppConfig): string {
  const { numbers, message, namespace = "default" } = config;

  if (typeof window === "undefined") {
    return buildLink(numbers[0], message);
  }

  const storageKey = `wa_assigned_${namespace}`;

  try {
    const saved = localStorage.getItem(storageKey);
    if (saved && numbers.includes(saved)) {
      return buildLink(saved, message);
    }

    const assigned = numbers[Math.floor(Math.random() * numbers.length)];
    localStorage.setItem(storageKey, assigned);
    return buildLink(assigned, message);
  } catch {
    // localStorage unavailable (private mode, storage blocked) — pick randomly
    // each time rather than hard-coding index 0, so the load still spreads.
    return buildLink(numbers[Math.floor(Math.random() * numbers.length)], message);
  }
}

function buildLink(phone: string, message: string): string {
  return `https://api.whatsapp.com/send?phone=${phone}&text=${encodeURIComponent(message)}`;
}

/**
 * Convenience wrapper — builds the link and opens it in a new tab.
 */
export function openWhatsApp(config: WhatsAppConfig): void {
  window.open(getWhatsAppLink(config), "_blank", "noopener,noreferrer");
}

/**
 * Returns the pre-filled WhatsApp message based on the utm_source query param.
 */
export function getUtmMessage(): string {
  if (typeof window === "undefined") {
    return "Hi! I was on your website and interested in setting up a Hong Kong company. Can you give more info?";
  }
  const params = new URLSearchParams(window.location.search);
  const source = (params.get("utm_source") ?? "").toLowerCase();

  if (source === "google") {
    return "Hi! I came across MuteTaxes through Google. Looking to set up a Hong Kong company for tax optimisation. Can I know more?";
  }
  if (source === "facebook") {
    return "Hey! I came across MuteTaxes through Facebook/Instagram. Interested in Hong Kong company formation to reduce my taxes. Can I know more?";
  }
  return "Hi! I was on your website and interested in setting up a Hong Kong company. Can you give more info?";
}

/**
 * Opens WhatsApp with number rotation + UTM-based message.
 * Drop-in replacement for openWhatsApp when you want dynamic messages.
 */
export function openWhatsAppWithUtm(
  numbers: string[],
  namespace = "default"
): void {
  openWhatsApp({ numbers, message: getUtmMessage(), namespace });
}
